import React, { useState, useCallback } from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import EditBreadcrumbs from 'components/EditBreadcumbs';
import { updateUserPhoneNumber } from 'services/firebase/update';

import { styles } from './styles';

const EditPhone: React.FC = () => {
  const { personalData, updatePersonalData } = useSubscription();
  const [phone, setPhone] = useState(personalData?.phoneNumber || '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const formatPhoneNumber = useCallback((value: string) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');

    // Apply Brazilian phone number formatting
    if (numbers.length <= 10) {
      // Landline format: (XX) XXXX-XXXX
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else {
      // Mobile format: (XX) XXXXX-XXXX
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
  }, []);

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const numbers = value.replace(/\D/g, '');

      // Limit to 11 digits
      if (numbers.length <= 11) {
        setPhone(formatPhoneNumber(value));
        setError(null);
      }
    },
    [formatPhoneNumber],
  );

  const validatePhone = useCallback((phoneNumber: string) => {
    const numbers = phoneNumber.replace(/\D/g, '');

    if (numbers.length < 10) {
      return 'Telefone deve ter pelo menos 10 dígitos';
    }

    if (numbers.length > 11) {
      return 'Telefone deve ter no máximo 11 dígitos';
    }

    // Validate area code (first 2 digits)
    const areaCode = numbers.substring(0, 2);
    if (parseInt(areaCode) < 11 || parseInt(areaCode) > 99) {
      return 'Código de área inválido';
    }

    return null;
  }, []);

  const handleSave = useCallback(async () => {
    setError(null);
    setLoading(true);

    if (!phone.trim()) {
      setError('Telefone é obrigatório');
      setLoading(false);
      return;
    }

    const validationError = validatePhone(phone);
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      // Update phone number in Firebase
      await updateUserPhoneNumber(phone);

      // Update local context to reflect the change immediately
      updatePersonalData({ phoneNumber: phone });

      navigate('/my-account');
    } catch (error: any) {
      console.error('Erro ao salvar telefone:', error);
      setError(error.message || 'Erro ao salvar telefone. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [phone, validatePhone, navigate, updatePersonalData]);

  return (
    <Box sx={styles.container}>
      <EditBreadcrumbs current="Editar telefone" />
      <Typography variant="h5" sx={styles.title}>
        Altere seu telefone
      </Typography>
      <Typography sx={styles.description}>
        Se quiser alterar o telefone associado à sua conta, você poderá fazê-lo
        a seguir. Lembre-se de clicar no botão{' '}
        <strong>Salvar alterações</strong> quando tiver concluído.
      </Typography>
      <TextField
        fullWidth
        label="Novo telefone"
        value={phone}
        onChange={handlePhoneChange}
        placeholder="(16) 99999-9999"
        helperText="Digite apenas números. A formatação será aplicada automaticamente."
        sx={styles.input}
        error={!!error}
      />
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <Button
        variant="contained"
        sx={styles.button}
        onClick={handleSave}
        disabled={loading}
      >
        {loading ? 'Salvando...' : 'Salvar alterações'}
      </Button>
    </Box>
  );
};

export default EditPhone;
